import React from "react";
import { z } from "zod";

import { type DocumentSet } from "@hsl/core/documents/types";
import { type EventDocument } from "@hsl/core/hooks/useYardAnalyticsEvents";
import cx from "@hsl/core/utils/cx";
import { type FundDataWithType } from "@hsl/fund-explorer/metaSchemas";
import { type TopLevelDataObj } from "@hsl/fund-explorer/types";

import CSVIcon from "../../CsvIcon/CsvIcon";
import PDFIcon from "../../PdfIcon/PdfIcon";
import DocumentCheckbox from "./DocumentCheckbox";
import { DocElem } from "./DocumentDisplay/components";
import NullReplacement from "./NullReplacement";

const fundDocSchema = z.object({
    id: z.number(),
    name: z.string(),
    lang: z.string().optional(),
    url: z.string(),
    date: z.string().optional(),
});

interface Props {
    selectionActive?: boolean;
    className?: string;
    containerClassName?: string;
    textContainerClassName?: string;
    dateClassName?: string;
    showDate?: boolean;
    showLanguage?: boolean;
    fund?: FundDataWithType;
    shareClass?: TopLevelDataObj;
    onDocDownload?(file: EventDocument): void;
}

export const FundDoc = ({
    data,
    selectionActive,
    className,
    containerClassName,
    textContainerClassName,
    dateClassName,
    showDate = true,
    showLanguage = false,
    fund,
    shareClass,
    onDocDownload,
}: { data: z.infer<typeof fundDocSchema> } & Props) => {
    const parsedData = fundDocSchema.safeParse(data);
    if (!parsedData.success) {
        return <NullReplacement />;
    }
    const { url, name, date, lang } = parsedData.data;
    const fileType = url.split(".").pop()?.toLowerCase();

    const handleDocLinkClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onDocDownload &&
            onDocDownload({
                date: date ?? "",
                name,
                type: fileType ?? "",
                url,
            });
    };

    return (
        <>
            <div
                className={cx(
                    "flex items-center justify-between",
                    containerClassName,
                )}
            >
                {/* TODO: get the link tracking that happens in DocElem to work here too! */}
                <a
                    href={url}
                    target="_blank"
                    className={cx("flex py-1 text-sm", className)}
                    onClick={handleDocLinkClick}
                    rel="noreferrer"
                >
                    <DocElem
                        icon={fileType === "pdf" ? <PDFIcon /> : <CSVIcon />}
                        name={name}
                        lang={lang as string}
                        asAtDate={date}
                        fund={fund}
                        shareClass={shareClass}
                    />
                    <div className={textContainerClassName}>
                        <span className="text-charcoal ml-2">
                            {name}
                            <span className="text-xs italic opacity-90">
                                {lang && showLanguage && ` (${lang})`}
                            </span>
                        </span>
                        {showDate && date && (
                            <div
                                className={cx(
                                    "text-taupe-grey pl-2",
                                    dateClassName,
                                )}
                            >
                                {date}
                            </div>
                        )}
                    </div>
                </a>
                {selectionActive && (
                    <DocumentCheckbox value={url} name={name} />
                )}
            </div>
        </>
    );
};

export function renderFundDoc(
    doc: DocumentSet,
    selectionActive: boolean,
    showDate = true,
    showLang = false,
) {
    return (
        <FundDoc
            key={doc.id}
            data={{
                id: doc.id,
                name: doc.language_aware_display_name,
                url: doc.url,
                date: doc.date,
            }}
            selectionActive={selectionActive}
            showDate={showDate}
            showLanguage={showLang}
        />
    );
}

export default FundDoc;
